import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IWeeklyRewardAllocations {
  crypto: number;
  options: number;
  equity: number;
  commodity: number;
  rwa: number;
  crypto_majors: number;
}

// useWeeklyRewardAllocations wraps GET/POST /weekly-reward-allocations.
// The six values must sum to 1,000,000 AEVO; the backend enforces this and
// returns 400 with an explanation on mismatch. Status is polled every 30s so
// the UI reflects updates from other admins without manual refresh.
export const useWeeklyRewardAllocations = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();

  const endpoint = ADMIN_API_URL(AdminEndpointEnum.WEEKLY_REWARD_ALLOCATIONS);
  const swr = useSWR<IWeeklyRewardAllocations, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<Partial<IWeeklyRewardAllocations>>(
        endpoint,
        queryConfig()
      );
      // Coerce missing fields to 0 so the UI renders cleanly against
      // backends that haven't shipped a newer category yet (e.g. a fresh
      // frontend on top of an older exchange-backend before deploy).
      const raw = response.data || {};
      return {
        crypto: raw.crypto ?? 0,
        options: raw.options ?? 0,
        equity: raw.equity ?? 0,
        commodity: raw.commodity ?? 0,
        rwa: raw.rwa ?? 0,
        crypto_majors: raw.crypto_majors ?? 0,
      };
    },
    { refreshInterval: 30000 }
  );

  const withAuthHeaders = useCallback(
    () => ({
      headers: {
        Authorization: `Bearer ${tokenId}`,
      },
    }),
    [tokenId]
  );

  const updateWeeklyRewardAllocations = useCallback(
    async (alloc: IWeeklyRewardAllocations) => {
      try {
        await axios.post(
          endpoint,
          {
            weekly_reward_crypto: alloc.crypto,
            weekly_reward_options: alloc.options,
            weekly_reward_equity: alloc.equity,
            weekly_reward_commodity: alloc.commodity,
            weekly_reward_rwa: alloc.rwa,
            weekly_reward_crypto_majors: alloc.crypto_majors,
          },
          withAuthHeaders()
        );
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Weekly allocations updated",
            subheader: `Crypto Majors: ${alloc.crypto_majors.toLocaleString()} | Options: ${alloc.options.toLocaleString()} | Crypto: ${alloc.crypto.toLocaleString()} | Equity: ${alloc.equity.toLocaleString()} | Commodity: ${alloc.commodity.toLocaleString()} | RWA: ${alloc.rwa.toLocaleString()}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
        swr.mutate();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        const payload = (error as AxiosError)?.response?.data as any;
        const subheader = payload?.error || payload?.message || "Unknown error";
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Update failed",
            subheader,
            status: ToastStatusEnum.ERROR,
          },
          5000
        );
      }
    },
    [addToast, endpoint, swr, withAuthHeaders]
  );

  return {
    ...swr,
    updateWeeklyRewardAllocations,
  };
};
